<template>
  <div ref="customer" class="row gx-5 mt-60 flex-column-reverse flex-xl-row">
    <div class="col-lg-12 col-xl-8">
      <Loader v-if="isLoading" display="absolute"></Loader>
      <Alert
        v-if="errorMsgs.length"
        type="danger"
        :messages="errorMsgs"></Alert>

      <form autocomplete="off" @submit.prevent="onSubmitForm">
        <div class="row h3 mb-8">
          <span class="col bold">{{ dictionary.step2Label }}</span>
        </div>
        <div class="row mb-24">
          <div class="col">{{ dictionary.step2SecondLabel }}</div>
        </div>
        <div class="row">
          <div class="col">
            <!-- customer type -->
            <div class="row mb-28">
              <div class="col">
                <label for="customerType" class="form-label">
                  {{ dictionary.customerTypeLabel }}
                </label>
                <select
                  v-model="customerType"
                  class="form-select"
                  :class="{ 'has-error': v$.customerType.$error }"
                  @change="onChangeSort">
                  <option value="" disabled selected>
                    {{ dictionary.customerTypePlaceholder }}
                  </option>
                  <option
                    v-for="item in customerTypes"
                    :key="item.value"
                    :value="item.value">
                    {{ item.text }}
                  </option>
                </select>
              </div>
              <div v-if="v$.customerType.$error">
                <div v-if="v$.customerType.required.$invalid" class="error">
                  {{ dictionary.customerTypeRequired }}
                </div>
              </div>
            </div>
            <div v-if="extraFields" class="row gx-5">
              <!-- brandName -->
              <div class="col-lg-6 mb-28">
                <label for="brandName" class="form-label">
                  {{ dictionary.brandNameLabel }}
                </label>
                <input
                  id="brandName"
                  v-model.trim="brandName"
                  class="form-control"
                  :class="{ 'has-error': v$.brandName.$error }"
                  name="brandName"
                  type="text"
                  maxlength="30"
                  @blur="v$.brandName.$touch" />

                <div v-if="v$.brandName.$error">
                  <div v-if="v$.brandName.requiredIf.$invalid" class="error">
                    {{ dictionary.brandNameRequired }}
                  </div>
                </div>
              </div>
              <!-- vatNumber -->
              <div class="col-lg-6 mb-28">
                <label for="vatNumber" class="form-label">
                  {{ dictionary.vatNumberLabel }}
                </label>
                <input
                  id="vatNumber"
                  v-model.trim="vatNumber"
                  class="form-control"
                  :class="{ 'has-error': v$.vatNumber.$error }"
                  name="vatNumber"
                  type="text"
                  maxlength="30"
                  @blur="v$.vatNumber.$touch" />

                <div v-if="v$.vatNumber.$error">
                  <div v-if="v$.vatNumber.requiredIf.$invalid" class="error">
                    {{ dictionary.vatNumberRequired }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row gx-5">
              <!-- name -->
              <div class="col-lg-6 mb-28">
                <label for="name" class="form-label">
                  {{ dictionary.nameLabel }}
                </label>
                <input
                  id="name"
                  v-model.trim="name"
                  class="form-control"
                  :class="{ 'has-error': v$.name.$error }"
                  name="name"
                  type="text"
                  @blur="v$.name.$touch" />

                <div v-if="v$.name.$error">
                  <div v-if="v$.name.required.$invalid" class="error">
                    {{ dictionary.nameRequired }}
                  </div>
                  <!-- prettier-ignore -->
                  <div
                  v-if="v$.name.minLength.$invalid || v$.name.maxLength.$invalid"
                  class="error">
                  {{ dictionary.nameLength }}
                </div>
                </div>
              </div>
              <!-- surname -->
              <div class="col-lg-6 mb-28">
                <label for="surname" class="form-label">
                  {{ dictionary.surnameLabel }}
                </label>
                <input
                  id="surname"
                  v-model.trim="surname"
                  class="form-control"
                  :class="{ 'has-error': v$.surname.$error }"
                  name="surname"
                  type="text"
                  @blur="v$.surname.$touch" />

                <div v-if="v$.surname.$error">
                  <div v-if="v$.surname.required.$invalid" class="error">
                    {{ dictionary.surnameRequired }}
                  </div>
                  <!-- prettier-ignore -->
                  <div
                  v-if="v$.surname.minLength.$invalid || v$.surname.maxLength.$invalid"
                  class="error">
                  {{ dictionary.surnameLength }}
                </div>
                </div>
              </div>
            </div>
            <div class="row gx-5">
              <!-- address -->
              <div class="col-lg-6 mb-28">
                <label for="address" class="form-label">
                  {{ dictionary.addressLabel }}
                </label>
                <input
                  id="address"
                  v-model.trim="address"
                  class="form-control"
                  :class="{ 'has-error': v$.address.$error }"
                  name="address"
                  type="text"
                  @blur="v$.address.$touch" />

                <div v-if="v$.address.$error">
                  <div v-if="v$.address.required.$invalid" class="error">
                    {{ dictionary.addressRequired }}
                  </div>
                </div>
              </div>
              <!-- city -->
              <div class="col-lg-6 mb-28">
                <label for="city" class="form-label">
                  {{ dictionary.cityLabel }}
                </label>
                <input
                  id="city"
                  v-model.trim="city"
                  class="form-control"
                  :class="{ 'has-error': v$.city.$error }"
                  name="city"
                  type="text"
                  @blur="v$.city.$touch" />

                <div v-if="v$.city.$error">
                  <div v-if="v$.city.required.$invalid" class="error">
                    {{ dictionary.cityRequired }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row gx-5">
              <!-- email -->
              <div class="col-lg-6 mb-28">
                <label for="email" class="form-label">
                  {{ dictionary.emailLabel }}
                </label>
                <input
                  id="email"
                  v-model.trim="email"
                  class="form-control"
                  :class="{ 'has-error': v$.email.$error }"
                  name="email"
                  type="email"
                  @blur="v$.email.$touch" />

                <div v-if="v$.email.$error">
                  <div v-if="v$.email.required.$invalid" class="error">
                    {{ dictionary.emailRequired }}
                  </div>
                  <div v-if="v$.email.email.$invalid" class="error">
                    {{ dictionary.emailRegex }}
                  </div>
                  <!-- prettier-ignore -->
                  <div
                  v-if="v$.email.minLength.$invalid || v$.email.maxLength.$invalid"
                  class="error">
                  {{ dictionary.emailLength }}
                </div>
                </div>
              </div>
              <!-- confirmEmail -->
              <div class="col-lg-6 mb-28">
                <label for="confirmEmail" class="form-label">
                  {{ dictionary.confirmEmailLabel }}
                </label>
                <input
                  id="confirmEmail"
                  v-model.trim="confirmEmail"
                  class="form-control"
                  :class="{ 'has-error': v$.confirmEmail.$error }"
                  name="confirmEmail"
                  type="text"
                  @paste="(e) => e.preventDefault()"
                  @drop="(e) => e.preventDefault()"
                  @blur="v$.confirmEmail.$touch" />

                <div v-if="v$.confirmEmail.$error">
                  <div v-if="v$.confirmEmail.required.$invalid" class="error">
                    {{ dictionary.confirmEmailRequired }}
                  </div>
                  <div v-if="v$.confirmEmail.email.$invalid" class="error">
                    {{ dictionary.emailRegex }}
                  </div>
                  <!-- prettier-ignore -->
                  <div
                  v-if="v$.confirmEmail.minLength.$invalid || v$.confirmEmail.maxLength.$invalid"
                  class="error">
                  {{ dictionary.emailLength }}
                </div>
                  <div
                    v-if="v$.confirmEmail.sameAsPassword.$invalid"
                    class="error">
                    {{ dictionary.confirmEmailSameAs }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row gx-5">
              <!-- phone -->
              <div class="col-lg-6 mb-28">
                <label for="phone" class="form-label">
                  {{ dictionary.phoneLabel }}
                </label>
                <div class="phone-group">
                  <MultiSelect
                    id="dialingCode"
                    v-model="dialingCode"
                    autocomplete="nope"
                    :searchable="true"
                    :clear-on-search="true"
                    :options="dialingCodes"
                    :can-deselect="false"
                    :can-clear="false"
                    :no-results-text="dictionary.noResultsText"
                    label="text">
                    <template #singlelabel="{ value }">
                      <div class="multiselect-single-label">
                        <img
                          class="multiselect-icon-currency"
                          :src="value.icon" />
                        {{ value.text }}
                      </div>
                    </template>

                    <template #option="{ option }">
                      <img
                        class="multiselect-icon-currency"
                        :src="option.icon" />
                      {{ option.text }}
                    </template>
                  </MultiSelect>

                  <input
                    id="phone"
                    v-model.trim="phone"
                    class="form-control"
                    :class="{ 'has-error': v$.phone.$error }"
                    name="phone"
                    type="tel"
                    @blur="v$.phone.$touch" />
                </div>

                <div v-if="v$.phone.$error">
                  <div v-if="v$.phone.required.$invalid" class="error">
                    {{ dictionary.phoneRequired }}
                  </div>
                  <!-- prettier-ignore -->
                  <div
                    v-if="v$.phone.minLength.$invalid || v$.phone.maxLength.$invalid"
                    class="error">
                    {{ dictionary.phoneLength }}
                  </div>
                  <div v-if="v$.phone.numeric.$invalid" class="error">
                    {{ dictionary.phoneNumeric }}
                  </div>
                </div>
              </div>
              <!-- phone type -->
              <div class="col-lg-6 mb-28">
                <label class="form-label bold mb-24">
                  {{ dictionary.phoneTypeLabel }}
                </label>
                <div class="form-check-list">
                  <div
                    v-for="index in phoneTypes"
                    :key="index.id"
                    class="form-check">
                    <input
                      :id="index.id"
                      v-model="phoneType"
                      class="form-check-input"
                      type="radio"
                      name="phoneTypes"
                      :value="index.value"
                      :checked="index.selected" />
                    <label :for="index.id" class="form-check-label">
                      {{ index.text }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-separator mb-28"></div>

            <div class="row mb-28">
              <!-- already customer -->
              <div class="col">
                <input
                  id="member"
                  v-model="isChecked"
                  class="form-check-input"
                  type="checkbox" />
                <label for="member" class="form-check-label semibold">
                  {{ dictionary.memberLabel }}
                </label>
              </div>
            </div>
            <div v-show="isChecked" class="row mb-28">
              <div class="col-lg-6">
                <label
                  class="form-label form-label--tooltip"
                  for="memberCode"
                  data-bs-toggle="tooltip"
                  data-bs-html="true"
                  :title="dictionary.memberCodeTooltipText">
                  <span>{{ dictionary.memberCodeLabel }}</span>
                  <LazyImage
                    :image="{ url: '/imgs/icons/tooltip.svg' }"
                    width="16"
                    alt="Tooltip" />
                </label>
                <input
                  id="memberCode"
                  v-model.trim="memberCode"
                  class="form-control"
                  :class="{ 'has-error': v$.memberCode.$error }"
                  type="text"
                  maxlength="30"
                  @blur="v$.memberCode.$touch" />

                <div v-if="v$.memberCode.$error">
                  <div v-if="v$.memberCode.requiredIf.$invalid" class="error">
                    {{ dictionary.memberCodeRequired }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-28">
              <!-- use personal data -->
              <div class="col">
                <input
                  id="usePersonalData"
                  v-model="usePersonalData"
                  class="form-check-input"
                  type="checkbox" />
                <label for="usePersonalData" class="form-check-label semibold">
                  <div v-html="dictionary.usePersonalDataLabel"></div>
                </label>
              </div>
            </div>
            <div class="row mb-60">
              <!-- accept terms -->
              <div class="col">
                <input
                  id="accept-terms"
                  v-model="terms"
                  class="form-check-input"
                  :class="{ 'has-error': v$.terms.$error }"
                  name="AcceptTerms"
                  type="checkbox"
                  autocomplete="off" />
                <label for="accept-terms" class="form-check-label semibold">
                  <div v-html="dictionary.termsLabel"></div>
                </label>
                <div v-if="v$.terms.$error">
                  <div v-if="v$.terms.checked.$invalid" class="error">
                    {{ dictionary.termsRequired }}
                  </div>
                </div>
              </div>
            </div>
            <!-- buttons -->
            <div class="row">
              <div class="col-lg-4">
                <div id="qr-recaptcha" class="mb-20"></div>

                <div v-if="v$.reCaptchaResponse.$error">
                  <div
                    v-if="v$.reCaptchaResponse.required.$invalid"
                    class="error">
                    {{ dictionary.reCaptchaRequired }}
                  </div>
                </div>
              </div>
              <div class="col-lg-5 offset-lg-3 col-xl-6 offset-xl-2 mb-20">
                <button
                  type="submit"
                  class="btn btn-primary btn-full-width"
                  :disabled="isLoading">
                  {{ dictionary.buttonSubmitText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="col-xl-4">
      <div v-for="(item, index) in vehiclesData" :key="index" class="mb-24">
        <VehicleResume
          :vehicle="item"
          :panel-number="index"
          :editable="true"
          :enable-buy-out="enableBuyOut"
          :enable-payment-in-advance="enablePaymentInAdvance"
          @get-step="editVehicle" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as lib from '../../../lib/lib'
import MultiSelect from '@vueform/multiselect'
import LazyImage from '../../components/common/LazyImage.vue'
import useVuelidate from '@vuelidate/core'
import {
  email,
  maxLength,
  minLength,
  numeric,
  required,
  requiredIf,
  sameAs,
} from '@vuelidate/validators'
import VehicleResume from './VehicleResume.vue'
import Alert from './../common/Alert.vue'
import Loader from './../common/Loader.vue'

export default {
  components: {
    MultiSelect,
    VehicleResume,
    LazyImage,
    Alert,
    Loader,
  },
  props: {
    step: {
      required: true,
      type: Number,
    },
    vehiclesData: {
      required: true,
      type: Array,
    },
  },
  emits: ['get-step', 'customer-data'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isChecked: false,
      scrollOffsetY: 125,
      isLoading: false,
      errorMsgs: [],

      // form properties
      customerType: '',
      brandName: '',
      vatNumber: '',

      name: '',
      surname: '',
      email: '',
      confirmEmail: '',

      dialingCode: '',
      phone: '',
      phoneType: '',

      address: '',
      city: '',

      memberCode: '',

      usePersonalData: false,
      terms: false,

      reCaptchaResponse: '',
      reCaptchaWidget: null,
      customerData: [],

      // data sources
      customerTypes: [],
      dialingCodes: [],
      phoneTypes: [],

      enableBuyOut: false,
      enablePaymentInAdvance: false,
    }
  },
  validations() {
    return {
      customerType: {
        required,
      },
      brandName: {
        requiredIf: requiredIf(this.extraFields),
      },
      vatNumber: {
        requiredIf: requiredIf(this.extraFields),
      },
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      surname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
      },
      email: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
      },
      confirmEmail: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
        sameAsPassword: sameAs(this.email),
      },
      phone: {
        required,
        numeric,
        minLength: minLength(5),
        maxLength: maxLength(40),
      },
      dialingCode: {
        required,
      },
      address: {
        required,
      },
      city: {
        required,
      },
      memberCode: {
        requiredIf: requiredIf(this.isChecked),
      },
      terms: {
        checked: (value) => value === true,
      },
      reCaptchaResponse: {
        required,
      },
    }
  },
  computed: {
    extraFields() {
      if (!this.customerTypes || !this.customerType) {
        return false
      }
      if (
        this.customerTypes.find((item) => item.value === this.customerType)
          .isBussiness
      ) {
        return true
      }
      return false
    },
    goNextStep() {
      return this.step + 1
    },
    goPreviousStep() {
      return this.step - 1
    },
  },
  mounted() {
    const {
      dialingCodes,
      phoneTypes,
      customerTypes,
      enableBuyOut,
      enablePaymentInAdvance,
    } = lib.getData('quotationData')

    this.dialingCodes = dialingCodes
    this.phoneTypes = phoneTypes
    this.customerTypes = customerTypes

    this.enableBuyOut = enableBuyOut
    this.enablePaymentInAdvance = enablePaymentInAdvance

    this.setPreselectedOptions()
    this.iniReCaptcha()

    lib.appendUrlParam('page', 'personal-details')

    this.$nextTick(() => {
      lib.scrollToTop()
      lib.iniTooltips(this.$refs.customer)
    })
  },
  methods: {
    callbackReCaptcha(response) {
      this.reCaptchaResponse = response
    },

    expiredCallbackReCaptha() {
      this.reCaptchaResponse = ''
    },

    iniReCaptcha() {
      document.getElementById('qr-recaptcha').innerHTML = ''

      const grecaptcha = window.grecaptcha

      this.reCaptchaWidget = grecaptcha.render('qr-recaptcha', {
        sitekey: this.configuration.reCaptchaPublicKey,
        callback: this.callbackReCaptcha,
        'expired-callback': this.expiredCallbackReCaptha,
      })
    },

    resetReCaptcha() {
      this.reCaptchaResponse = ''
      window.grecaptcha.reset(this.reCaptchaWidget)
    },

    setPreselectedOptions() {
      const entry = this.dialingCodes.find((i) => i.selected)
      this.dialingCode = entry ? entry.value : ''

      const entry2 = this.phoneTypes.find((i) => i.selected)
      this.phoneType = entry2 ? entry2.value : ''
    },

    collectData() {
      const customerData = {
        CustomerType: this.customerType,
        BrandName: this.brandName.trim(),
        VatNumber: this.vatNumber.trim(),
        Name: this.name.trim(),
        Surname: this.surname.trim(),
        Address: this.address.trim(),
        City: this.city.trim(),
        Email: this.email.trim(),
        DialingCode: this.dialingCode,
        Phone: this.phone.trim(),
        PhoneType: this.phoneType,
        MemberCode: this.memberCode.trim(),
        UsePersonalData: this.usePersonalData,
        Terms: this.terms,
        'g-recaptcha-response': this.reCaptchaResponse,
      }
      this.customerData = customerData

      this.$emit('customer-data', this.customerData)
    },

    onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      this.collectData()

      const postData = {
        vehiclesData: this.vehiclesData,
        customerData: this.customerData,
      }

      axios
        .post(this.configuration.endpointSubmitData, postData)
        .then((response) => {
          const { hasError, errorMessages } = response.data
          // response has error
          if (hasError) {
            this.handleError(errorMessages)
            return false
          }

          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
      this.$emit('get-step', this.goNextStep)
    },

    handleError(messages) {
      this.resetReCaptcha()
      this.errorMsgs = messages
      this.isLoading = false
      this.$nextTick(() => {
        lib.scrollToError(150)
      })
    },

    editVehicle() {
      this.$emit('get-step', this.goPreviousStep)
    },
  },
}
</script>
